import { Checkbox, Modal } from '@mantine/core'
import { useEffect, useState } from 'react'
import { IconCalendarEvent, IconTrash, IconX } from '@tabler/icons-react'
import { CANCEL_BTN, MODAL_CONTENT, SUBMIT_BTN } from '@/components/ui/formModal.tokens'
import { fmtTime, type CalendarEvent } from '../scheduleCalendar.tokens'

export interface CancelAppointmentModalProps {
  opened: boolean
  /**
   * The calendar event being cancelled, used to restate it. Optional: the
   * cancel action carries only an encounter id, so a caller that cannot resolve
   * the event still gets a working confirmation, just without the detail box.
   */
  appointment?: CalendarEvent
  /** True while the cancellation request is in flight. */
  cancelling?: boolean
  onClose: () => void
  /**
   * `cancelZocdoc` reflects the checkbox, and is meaningful only for a Zocdoc-bound visit — it
   * comes back false for every other appointment, which have no upstream booking to cancel.
   */
  onConfirm: (options: { cancelZocdoc: boolean }) => void
}

/**
 * Confirmation step in front of cancelling an appointment.
 *
 * Cancelling notifies the patient and cannot be undone from this screen, so it
 * is not left as a single click on the preview popup. The appointment is
 * restated here — patient, when, and what for — because the popup is already
 * closed by the time this opens, leaving the user nothing else to check against.
 */
export const CancelAppointmentModal = ({
  opened,
  appointment,
  cancelling = false,
  onClose,
  onConfirm,
}: CancelAppointmentModalProps) => {
  const zocdocAppointmentId = appointment?.zocdocAppointmentId
  // Ticked by default: the two calendars disagreeing is the worse outcome, and a booking left
  // live on Zocdoc keeps offering a visit nobody will attend.
  const [cancelZocdoc, setCancelZocdoc] = useState(true)
  // Re-armed per opening, so a coordinator who unticked it for one appointment does not silently
  // carry that choice into the next one.
  useEffect(() => {
    if (opened) setCancelZocdoc(true)
  }, [opened])

  const when =
    appointment?.start && appointment.end
      ? `${new Date(appointment.start).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })} · ${fmtTime(appointment.start)}–${fmtTime(appointment.end)}`
      : ''

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      classNames={{ content: MODAL_CONTENT, body: '!p-0' }}
      radius={18}
      size="440px"
      // Above the appointment preview popover, which stays open behind this
      // dialog and otherwise wins on z-index (Mantine: popover 300, modal 200).
      zIndex={400}
    >
      <div className="flex flex-col gap-4 px-6 pt-5 pb-6">
        <div className="flex items-start justify-between">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FDECE8] text-[#E5533D]">
            <IconTrash size={20} stroke={2} />
          </span>
          <button
            type="button"
            title="Close"
            aria-label="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
          >
            <IconX size={17} stroke={2.1} />
          </button>
        </div>

        <div>
          <h2 className="m-0 text-[22px] font-extrabold tracking-[-0.4px] text-[#1F2733]">
            Cancel appointment?
          </h2>
          <p className="mt-1.5 mb-0 text-[13.5px] text-[#6B7280]">
            {appointment?.title
              ? `Are you sure you want to cancel ${appointment.title}'s appointment?`
              : 'Are you sure you want to cancel this appointment?'}
          </p>
        </div>

        {when && (
          <div className="flex items-start gap-2.5 rounded-[10px] border border-[#EEF0F2] px-3.5 py-3">
            <IconCalendarEvent size={18} stroke={2} className="mt-0.5 shrink-0 text-[#8A929C]" />
            <div className="min-w-0">
              <div className="text-[13.5px] font-bold text-[#1F2733]">{when}</div>
              {/* Visit reason, when the encounter carries one — same source as the
                  preview popup's reason badge. */}
              {appointment?.reasonLabel && (
                <div className="text-[13px] text-[#8A929C]">{appointment.reasonLabel}</div>
              )}
            </div>
          </div>
        )}

        {zocdocAppointmentId && (
          <Checkbox
            checked={cancelZocdoc}
            onChange={(event) => setCancelZocdoc(event.currentTarget.checked)}
            disabled={cancelling}
            label="Also cancel the Zocdoc booking"
            description={
              cancelZocdoc
                ? 'This visit came from Zocdoc. The Zocdoc booking will be cancelled too.'
                : 'Only the beHuman visit will be cancelled — the patient’s Zocdoc booking stays live.'
            }
            classNames={{ label: 'text-[13.5px] font-semibold text-[#1F2733]' }}
          />
        )}

        <p className="m-0 text-[12.5px] text-[#8A929C]">
          The patient will be notified of this cancellation.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button type="button" className={CANCEL_BTN} onClick={onClose} disabled={cancelling}>
            Keep appointment
          </button>
          <button
            type="button"
            className={SUBMIT_BTN}
            onClick={() =>
              onConfirm({ cancelZocdoc: Boolean(zocdocAppointmentId) && cancelZocdoc })
            }
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling…' : 'Cancel appointment'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
